import React, { useEffect, useRef, useState } from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NumericKeypad } from '../../../../../components/Keypad/NumericKeypad';
import classes from './general-item-modal.module.scss';
import {
  appendPriceDigit,
  appendQuantityDigit,
  backspaceBuffer,
  computeGeneralItemTotal,
  GENERAL_ITEM_DEFAULT_PRICE,
  GENERAL_ITEM_DEFAULT_QUANTITY,
  GENERAL_ITEM_NAME_MAX,
  isGeneralItemValid,
  parseGeneralItemNumber,
  readGeneralItemTargets,
  writeGeneralItemTargets,
  type GeneralItemField,
} from './general-item-modal';

export type GeneralItemPayload = {
  unitPrice: number;
  quantity: number;
  name: string | null;
  printerIds: number[];
  kds: boolean;
};

export type GeneralItemPrintTarget = { id: number; name: string };

export type GeneralItemModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  isManager: boolean;
  tenantId: string;
  activePrinters: GeneralItemPrintTarget[];
  isKdsAvailable: boolean;
  onClose: () => void;
  onConfirm: (payload: GeneralItemPayload) => void | Promise<void>;
};

const GeneralItemModalComponent: React.FC<GeneralItemModalProps> = ({
  isOpen,
  isSaving = false,
  isManager,
  tenantId,
  activePrinters,
  isKdsAvailable,
  onClose,
  onConfirm,
}) => {
  const [activeField, setActiveField] = useState<GeneralItemField>('price');
  const [quantityBuffer, setQuantityBuffer] = useState(GENERAL_ITEM_DEFAULT_QUANTITY);
  const [priceBuffer, setPriceBuffer] = useState(GENERAL_ITEM_DEFAULT_PRICE);
  const [isNegativePrice, setIsNegativePrice] = useState(false);
  const [name, setName] = useState('');
  const [selectedPrinterIds, setSelectedPrinterIds] = useState<number[]>([]);
  const [isKdsSelected, setIsKdsSelected] = useState(false);
  const freshRef = useRef<{ quantity: boolean; price: boolean }>({ quantity: true, price: true });
  const submitLockRef = useRef(false);

  const openContextRef = useRef({ tenantId, activePrinters, isKdsAvailable });
  openContextRef.current = { tenantId, activePrinters, isKdsAvailable };

  // Resets on the open transition only: printers/KDS arriving later must not wipe user input.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const context = openContextRef.current;
    setActiveField('price');
    setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
    setPriceBuffer(GENERAL_ITEM_DEFAULT_PRICE);
    setIsNegativePrice(false);
    setName('');
    const remembered = readGeneralItemTargets(
      context.tenantId,
      context.activePrinters.map((printer) => printer.id),
    );
    setSelectedPrinterIds(remembered.printerIds);
    setIsKdsSelected(remembered.kds && context.isKdsAvailable);
    freshRef.current = { quantity: true, price: true };
    submitLockRef.current = false;
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const pressDigit = (digit: string) => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      const fresh = freshRef.current.quantity;
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => appendQuantityDigit(fresh ? '' : prev, digit));
    } else {
      const fresh = freshRef.current.price;
      freshRef.current.price = false;
      setPriceBuffer((prev) => appendPriceDigit(fresh ? '' : prev, digit));
    }
  };

  const toggleNegative = () => {
    if (isSaving || !isManager) return;
    const next = !isNegativePrice;
    if (next) {
      // Refund lines are always quantity 1 (backend rejects >1); reset qty and move focus to price.
      setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
      freshRef.current.quantity = true;
      setActiveField('price');
    }
    setIsNegativePrice(next);
  };

  const togglePrinter = (printerId: number) => {
    if (isSaving) return;
    setSelectedPrinterIds((prev) => (
      prev.includes(printerId) ? prev.filter((id) => id !== printerId) : [...prev, printerId]
    ));
  };

  const pressBackspace = () => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => backspaceBuffer(prev));
    } else {
      freshRef.current.price = false;
      setPriceBuffer((prev) => backspaceBuffer(prev));
    }
  };

  const handleConfirm = () => {
    if (isSaving || submitLockRef.current) return;
    if (!isGeneralItemValid(quantityBuffer, priceBuffer)) return;
    if (isNegativePrice && !isManager) return;
    submitLockRef.current = true;
    const trimmedName = name.trim();
    const payload: GeneralItemPayload = {
      unitPrice: parseGeneralItemNumber(priceBuffer) * (isNegativePrice ? -1 : 1),
      quantity: parseGeneralItemNumber(quantityBuffer),
      name: isNegativePrice || trimmedName.length === 0 ? null : trimmedName,
      printerIds: isNegativePrice ? [] : selectedPrinterIds,
      kds: isNegativePrice ? false : isKdsSelected,
    };
    if (!isNegativePrice) {
      writeGeneralItemTargets(tenantId, { printerIds: selectedPrinterIds, kds: isKdsSelected });
    }
    void Promise.resolve(onConfirm(payload)).finally(() => {
      submitLockRef.current = false;
    });
  };

  const quantityDisplay = quantityBuffer.length > 0 ? quantityBuffer : '0';
  const priceDisplay = `${isNegativePrice ? '-' : ''}${priceBuffer.length > 0 ? priceBuffer : '0'}`;
  const canConfirm = !isSaving && isGeneralItemValid(quantityBuffer, priceBuffer);
  const totalPreview = computeGeneralItemTotal(quantityBuffer, priceBuffer) * (isNegativePrice ? -1 : 1);

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="פריט כללי"
      dir="rtl"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) {
          onClose();
        }
      }}
    >
      <div className={classes.modal}>
        <header className={classes.header} data-testid="general-item-header">
          {isNegativePrice ? (
            <h2 className={classes.title}>פריט כללי</h2>
          ) : (
            <TextInput
              type="text"
              variant="unstyled"
              classNames={{ root: classes.titleInputRoot, input: classes.titleInput }}
              dir="rtl"
              aria-label="שם הפריט"
              value={name}
              maxLength={GENERAL_ITEM_NAME_MAX}
              placeholder="פריט כללי"
              disabled={isSaving}
              onChange={(event) => setName(event.target.value)}
              data-testid="general-item-name-input"
            />
          )}
          {isNegativePrice ? (
            <p className={classes.refundBadge} data-testid="general-item-refund-badge">
              זיכוי
            </p>
          ) : (
            <p className={classes.subtitle}>לחץ על כמות או מחיר כדי לערוך</p>
          )}
        </header>

        <div className={classes.body}>
          <div className={`${classes.tiles} ${isNegativePrice ? classes.tilesRefund : ''}`}>
            {!isNegativePrice && (
              <button
                type="button"
                className={`${classes.tile} ${activeField === 'quantity' ? classes.tileActive : ''}`}
                onClick={() => setActiveField('quantity')}
                data-testid="general-item-quantity-tile"
              >
                <span className={classes.tileLabel}>כמות</span>
                <span className={classes.tileValue} data-testid="general-item-quantity-value">
                  {quantityDisplay}
                </span>
              </button>
            )}

            <button
              type="button"
              className={`${classes.tile} ${activeField === 'price' ? classes.tileActive : ''}`}
              onClick={() => setActiveField('price')}
              data-testid="general-item-price-tile"
            >
              <span className={classes.tileLabel}>מחיר פריט בודד</span>
              <span
                className={`${classes.tileValue} ${isNegativePrice ? classes.tileValueNegative : ''}`}
                data-testid="general-item-price-value"
              >
                ₪ {priceDisplay}
              </span>
            </button>
          </div>

          {!isNegativePrice && (activePrinters.length > 0 || isKdsAvailable) && (
            <div className={classes.targetsField}>
              <span className={classes.targetsLabel}>יעדי הדפסה</span>
              <div className={classes.targetsGrid}>
                {activePrinters.map((printer) => (
                  <UnstyledButton
                    key={printer.id}
                    component="button"
                    type="button"
                    className={`${classes.targetTile} ${selectedPrinterIds.includes(printer.id) ? classes.targetTileActive : ''}`}
                    onClick={() => togglePrinter(printer.id)}
                    disabled={isSaving}
                    aria-pressed={selectedPrinterIds.includes(printer.id)}
                    data-testid={`general-item-target-printer-${printer.id}`}
                  >
                    {printer.name}
                  </UnstyledButton>
                ))}
                {isKdsAvailable && (
                  <UnstyledButton
                    component="button"
                    type="button"
                    className={`${classes.targetTile} ${isKdsSelected ? classes.targetTileActive : ''}`}
                    onClick={() => setIsKdsSelected((prev) => !prev)}
                    disabled={isSaving}
                    aria-pressed={isKdsSelected}
                    data-testid="general-item-target-kds"
                  >
                    KDS
                  </UnstyledButton>
                )}
              </div>
            </div>
          )}

          <NumericKeypad
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            dataTestId="general-item-keypad"
            disabled={isSaving}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={isManager ? {
              label: '−',
              onClick: toggleNegative,
              disabled: isSaving,
              className: `${classes.key} ${classes.minusKey} ${isNegativePrice ? classes.minusKeyActive : ''}`,
              ariaLabel: 'זיכוי',
              dataTestId: 'general-item-minus-key',
            } : undefined}
          />

          <div className={classes.totalRow} data-testid="general-item-total">
            <span className={classes.totalBreakdown}>
              ₪ {priceDisplay} × {quantityDisplay}
            </span>
            <span className={classes.totalAmount}>
              <span className={classes.totalLabel}>סה״כ</span>
              <span
                className={`${classes.totalValue} ${isNegativePrice ? classes.totalValueNegative : ''}`}
              >
                <span dir="ltr">{totalPreview}</span> ₪
              </span>
            </span>
          </div>

          <div className={classes.actions}>
            <button
              type="button"
              className={classes.cancelButton}
              onClick={onClose}
              disabled={isSaving}
              data-testid="general-item-cancel"
            >
              ביטול
            </button>
            <button
              type="button"
              className={classes.confirmButton}
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="general-item-confirm"
            >
              {isSaving ? 'שומר...' : 'אישור'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GeneralItemModal = React.memo(GeneralItemModalComponent);
